import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { MTLogo } from './Logo';
import { useLanguage } from '../context/LanguageContext';

interface NavbarProps {}

const Navbar: React.FC<NavbarProps> = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { language, setLanguage, toggleLanguage, t, isUrdu } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    setIsOpen(false);
    if (href === '#' || href === '#home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const targetElement = document.querySelector(href);
    if (targetElement) {
      const navOffset = 80;
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    } else {
      window.location.hash = href;
    }
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    scrollToSection(href);
  };

  const navLinks = [
    { name: t.nav.home, href: '#home' },
    { name: t.nav.about, href: '#about' },
    { name: t.nav.services, href: '#services' },
    { name: t.nav.estimator, href: '#estimator' },
    { name: t.nav.work, href: '#portfolio' },
    { name: t.nav.aiVideos, href: '#ai-videos' },
    { name: t.nav.contact, href: '#contact' },
  ];

  return (
    <nav className={`fixed top-0 left-0 w-full z-[100] transition-all duration-300 bg-white/95 backdrop-blur-md border-b border-black shadow-xs ${scrolled ? 'py-3' : 'py-4'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex justify-between items-center relative">
        
        {/* Brand Identity */}
        <a 
          href="#home" 
          onClick={(e) => handleNavClick(e, '#home')}
          aria-label="M. Taibe - Back to Home"
          className="text-xl sm:text-2xl font-display font-bold tracking-tight text-slate-900 z-10 flex items-center gap-3 group shrink-0 cursor-pointer"
        >
          <div className="group-hover:scale-105 transition-transform">
            <MTLogo className="w-10 h-10 shadow-sm" />
          </div>
          <span>M. TAIBE</span>
        </a>

        {/* Centralized Navigation */}
        <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 items-center bg-slate-950/95 backdrop-blur-md border border-slate-800/90 px-6 py-2 rounded-full gap-6 shadow-lg shadow-black/10">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="relative text-[11px] font-bold text-slate-300 hover:text-white transition-all uppercase tracking-[0.14em] group cursor-pointer whitespace-nowrap"
            >
              <span className="relative z-10">{link.name}</span>
              <span className="absolute bottom-[-3px] left-0 w-full h-[2px] bg-indigo-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left rounded-full"></span>
            </a>
          ))}
        </div>

        {/* Right Actions: Language Switcher & Let's Talk CTA */}
        <div className="hidden md:flex items-center gap-3.5 z-10 shrink-0">
          {/* Language Toggle Pill */}
          <div 
            role="radiogroup" 
            aria-label="Language Selector"
            className="flex items-center p-1 bg-slate-100/90 border border-slate-200/90 rounded-full shadow-inner"
          >
            <button
              onClick={() => setLanguage('en')}
              aria-label="Switch to English"
              aria-checked={language === 'en'}
              role="radio"
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold transition-all cursor-pointer ${
                language === 'en'
                  ? 'bg-white text-indigo-600 shadow-xs scale-100'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <span>EN</span>
            </button>
            <button
              onClick={() => setLanguage('ur')}
              aria-label="اردو زبان میں تبدیل کریں"
              aria-checked={language === 'ur'}
              role="radio"
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer ${
                language === 'ur'
                  ? 'bg-indigo-600 text-white shadow-xs scale-100'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <span className="font-arabic">اردو</span>
            </button>
          </div>

          <button
            onClick={() => scrollToSection('#contact')}
            className="bg-slate-900 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl font-bold text-[11px] uppercase tracking-[0.15em] transition-all shadow-md active:scale-95 cursor-pointer whitespace-nowrap"
          >
            {t.nav.letsTalk}
          </button>
        </div>

        {/* Mobile View: Language Toggle & Menu Button */}
        <div className="md:hidden flex items-center gap-2.5">
          {/* Quick Mobile Language Switcher */}
          <button
            onClick={toggleLanguage}
            aria-label={`Switch language. Current: ${language === 'en' ? 'English' : 'Urdu'}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-100 text-slate-800 text-xs font-bold active:scale-95 cursor-pointer shadow-2xs"
          >
            <Globe size={14} className="text-indigo-600" />
            <span>{language === 'en' ? 'اردو' : 'EN'}</span>
          </button>

          <button 
            onClick={() => setIsOpen(!isOpen)} 
            aria-label={isOpen ? "Close Menu" : "Open Menu"}
            className={`p-2.5 rounded-xl border border-slate-200 transition-all cursor-pointer ${isOpen ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-900'}`}
          >
            {isOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      {isOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-white border-b border-slate-200 shadow-2xl animate-fade-in z-50">
          <div className="flex flex-col p-6 space-y-4 text-center">
            {/* Language Selector in Menu */}
            <div className="flex justify-center items-center pb-2 border-b border-slate-100">
              <div className="inline-flex p-1 bg-slate-100 rounded-full border border-slate-200">
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                    language === 'en' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-600'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => setLanguage('ur')}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                    language === 'ur' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600'
                  }`}
                >
                  اردو (Urdu)
                </button>
              </div>
            </div>

            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-base font-bold text-slate-800 hover:text-slate-600 uppercase tracking-[0.14em] py-2 transition-colors cursor-pointer"
              >
                {link.name}
              </a>
            ))}
            <button
              onClick={() => scrollToSection('#contact')}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3.5 rounded-xl font-bold uppercase tracking-[0.18em] shadow-md transition-colors cursor-pointer"
            >
              {t.nav.letsTalk}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;

